import React from 'react';
import { Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Formik, FormikHelpers, Form } from 'formik';
import * as Yup from 'yup';

import { BackButton, FormGroupControl, FormGroupSelect } from '@components/Base';
import { userClaimUpdate } from './action';
import { useRouteParam } from '@hooks/params';
import { ClaimStatuses } from '@models/Claim';
import { useInitialValues } from './hook';
import i18n from '@components/i18n';

export type FormValues = {
  status: ClaimStatuses;
  note: string;
  totalClaimedAmount: number;
  approvedAmount?: number;
};

const groupProps = { className: 'mb-3' };

const validationSchema = Yup.object({
  status: Yup.string().required(i18n.t('common:required')),
  note: Yup.string().required(i18n.t('common:required')),
  totalClaimedAmount: Yup.number().required(),
  approvedAmount: Yup.number()
    .when('status', {
      is: (status: ClaimStatuses) => status === ClaimStatuses.Paid,
      then: Yup.number().required(i18n.t('common:required')),
      otherwise: Yup.number().notRequired(),
    })
    .max(Yup.ref('totalClaimedAmount')),
});

const ClaimEdit = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const claimableType = useRouteParam('claimableType');
  const claimableId = useRouteParam('claimableId');
  const initialValues = useInitialValues() as FormValues;

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    dispatch(userClaimUpdate({ ...values, claimableType, claimableId }));
    setSubmitting(false);
  };

  return (
    <>
      <BackButton className="mb-4" />
      <Card>
        <Card.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid }) => (
              <Form>
                <FormGroupControl
                  name="totalClaimedAmount"
                  label={t('claimForm.totalClaimedAmount.label')}
                  groupProps={groupProps}
                  type="number"
                />

                <FormGroupControl
                  name="approvedAmount"
                  label={t('claimForm.approvedAmount.label')}
                  groupProps={groupProps}
                  type="number"
                />

                <FormGroupSelect name="status" label={t('common:status')} groupProps={groupProps}>
                  <option value={ClaimStatuses.Requested}>{t('claimStatuses.requested')}</option>
                  <option value={ClaimStatuses.Submitted}>{t('claimStatuses.submitted')}</option>
                  <option value={ClaimStatuses.Reviewing}>{t('claimStatuses.reviewing')}</option>
                  <option value={ClaimStatuses.Voided}>{t('claimStatuses.voided')}</option>
                  <option value={ClaimStatuses.Nonpayable}>{t('claimStatuses.nonPayable')}</option>
                  <option value={ClaimStatuses.Paid}>{t('claimStatuses.paid')}</option>
                </FormGroupSelect>

                <FormGroupControl as="textarea" name="note" label={t('common:note')} groupProps={groupProps} />

                <Button type="submit" disabled={isSubmitting || !isValid}>
                  {t('common:submit')}
                </Button>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </>
  );
};

export { ClaimEdit };
